import { siteText } from '../siteText'
import { useCallback, useEffect, useRef, useState, type RefObject, type SyntheticEvent } from 'react'
import Hls, { type ErrorData, type Events, type LevelLoadedData } from 'hls.js'
import { LoaderCircle, RefreshCw } from 'lucide-react'
import type { Source } from '../types'
import { LIVE_BUFFER_SECONDS, measureMediaLoadProgress, type MediaLoadProgress } from '../liveLoadingProgress'
import { LIVE_MAX_LATENCY_SECONDS, liveSyncTarget as calculateLiveSyncTarget, safeLivePlaybackPosition } from '../livePlayback'
import { HEALTHY_RECOVERY_WINDOW_MS, MAX_MEDIA_RECOVERY_ATTEMPTS, mediaOrigin, preservePlayback, refreshedMediaUrl } from '../mediaRecovery'
import { isPreviewWindow } from '../sourcePlayback'
import { useViewActive } from '../viewActivity'
import './LiveSourcePlayer.css'

export interface LivePlayback {
  /** HLS is used for both rolling captures and retained finite previews. */
  kind?: 'file' | 'hls'
  /** Omitted for an active capture for backwards-compatible API responses. */
  live?: boolean
  /** Served directly by the capturing Mac, without the cloud preview delay. */
  local?: boolean
  /** A preview window has its own media clock; it is not the complete recording. */
  scope?: 'recording' | 'preview-window'
  /** False when the preview has no verified offset into the source recording. */
  timeline_available?: boolean
  url: string
  asset_url?: string
  revision: string
  mime_type: string
  duration: number | null
}

export interface LiveSourcePlayerProps {
  playback: LivePlayback | null
  source: Source
  capturing: boolean
  onTimeUpdate: (time: number) => void
  videoRef: RefObject<HTMLVideoElement>
  /** Reports real media buffering. A null value means the browser has no measurable total yet. */
  onLoadProgress?: (progress: MediaLoadProgress) => void
  onRetryCapture?: () => void
  retryingCapture?: boolean
  onStateChange?: (state: PlayerState) => void
  /** Reports preview playback independently of whether capture is running. */
  onPlayingChange?: (playing: boolean) => void
  onMutedChange?: (muted: boolean) => void
  /** Lets the workspace reveal recovery controls without declaring media ready. */
  onPlaybackBlockedChange?: (blocked: boolean) => void
}

export type PlayerState = 'waiting' | 'ready' | 'empty' | 'error'

interface PreservePlayback {
  time: number
  shouldResume: boolean
}

interface AudioPreference {
  volume: number
  muted: boolean
}

const STALL_RECOVERY_DELAY_MS = 4000

function isHlsUrl(url: string) {
  return /\.m3u8(?:[?#]|$)/i.test(url)
}

function isHlsPlayback(playback: LivePlayback | null) {
  return Boolean(playback && (playback.kind === 'hls' || isHlsUrl(playback.url)))
}

function isLivePlayback(playback: LivePlayback | null) {
  return isHlsPlayback(playback) && playback?.live !== false
}

function seekableLiveEdge(element: HTMLVideoElement) {
  if (!element.seekable.length) return null
  return element.seekable.end(element.seekable.length - 1)
}

function liveSyncTarget(element: HTMLVideoElement, hls: Hls | null, bufferSeconds = LIVE_BUFFER_SECONDS, maxLatencySeconds = LIVE_MAX_LATENCY_SECONDS): number | null {
  const seekable = seekableLiveEdge(element)
  const seekableStart = element.seekable.length ? element.seekable.start(0) : null
  const sync = hls?.liveSyncPosition ?? null
  return calculateLiveSyncTarget(seekableStart, seekable, sync, bufferSeconds, maxLatencySeconds)
}

/**
 * Player for the live workspace.
 *
 * Active captures use the server's rolling HLS playlist so the browser can
 * stay attached to the newest segment. A disconnected desktop capture can
 * retain a finite ENDLIST HLS preview; that path uses the same HLS transport
 * while following ordinary finite-media playback rules. HLS is deliberately
 * managed outside React's `src` attribute: changing the monitor's polling
 * revision must not remount an active live source or send the viewer back to
 * 0:00.
 */
export function LiveSourcePlayer({ playback, source, capturing, onTimeUpdate, videoRef, onLoadProgress, onRetryCapture, retryingCapture = false, onStateChange, onPlayingChange, onMutedChange, onPlaybackBlockedChange }: LiveSourcePlayerProps) {
  const viewActive = useViewActive()
  const viewActiveRef = useRef(viewActive)
  viewActiveRef.current = viewActive
  const wasViewActiveRef = useRef(viewActive)
  const [videoSrc, setVideoSrc] = useState<string | null>(null)
  const [videoKey, setVideoKey] = useState('empty')
  const [retry, setRetry] = useState(0)
  const [mediaUrlOverride, setMediaUrlOverride] = useState<string | null>(null)
  const [mediaRecoveryAttempt, setMediaRecoveryAttempt] = useState(0)
  const [state, setState] = useState<PlayerState>('waiting')
  const [hasRenderedFrame, setHasRenderedFrame] = useState(false)
  useEffect(() => { onStateChange?.(state) }, [state, onStateChange])
  useEffect(() => {
    const element = videoRef.current
    const report = () => onPlayingChange?.(Boolean(element && !element.paused && !element.ended))
    const reportAudio = () => onMutedChange?.(Boolean(element?.muted))
    report()
    reportAudio()
    if (!element) return
    for (const event of ['play', 'pause', 'ended']) element.addEventListener(event, report)
    element.addEventListener('volumechange', reportAudio)
    return () => {
      for (const event of ['play', 'pause', 'ended']) element.removeEventListener(event, report)
      element.removeEventListener('volumechange', reportAudio)
    }
  }, [videoKey, videoSrc, videoRef, onPlayingChange, onMutedChange])
  const [error, setError] = useState<string | null>(null)
  const [playbackBlocked, setPlaybackBlocked] = useState(false)
  const [atLiveEdge, setAtLiveEdge] = useState(true)
  useEffect(() => { onPlaybackBlockedChange?.(playbackBlocked) }, [onPlaybackBlockedChange, playbackBlocked])
  const sourceKeyRef = useRef<string | null>(null)
  const activeSourceRef = useRef(source.id)
  const preserveRef = useRef<PreservePlayback | null>(null)
  const wantsPlaybackRef = useRef(false)
  const userPausedRef = useRef(false)
  const endedNaturallyRef = useRef(false)
  const hlsRef = useRef<Hls | null>(null)
  const recoveryHealthyTimerRef = useRef<number | null>(null)
  const stallRecoveryTimerRef = useRef<number | null>(null)
  const mediaRecoveryAttemptRef = useRef(0)
  const playbackModeRef = useRef<'live' | 'finite-hls' | 'file' | null>(null)
  const playbackTransportRef = useRef<'hls' | 'file' | null>(null)
  const previewWindowRef = useRef<boolean | null>(null)
  const liveEdgeRef = useRef<number | null>(null)
  const programmaticSeekRef = useRef(false)
  const renderedFrameRef = useRef(false)
  const audioPreferenceRef = useRef<AudioPreference | null>(null)

  const hlsPlayback = isHlsPlayback(playback)
  const live = isLivePlayback(playback)
  const bufferSeconds = playback?.local ? 2 : LIVE_BUFFER_SECONDS
  const maxLatencySeconds = playback?.local ? 6 : LIVE_MAX_LATENCY_SECONDS
  // Native HLS needs the refreshed boundary URL in the element's src as well;
  // both HLS modes use the effective URL so the browser does not keep retrying
  // an expired boundary.
  const effectivePlaybackUrl = mediaUrlOverride || playback?.url
  const playbackKey = playback
    ? hlsPlayback
      ? `${source.id}|hls|${live ? 'live' : 'finite'}|${effectivePlaybackUrl || playback.url}|${playback.revision}`
      : `${source.id}|file|${effectivePlaybackUrl}|${playback.revision}`
    : 'empty'

  useEffect(() => {
    if (recoveryHealthyTimerRef.current !== null) {
      window.clearTimeout(recoveryHealthyTimerRef.current)
      recoveryHealthyTimerRef.current = null
    }
    if (stallRecoveryTimerRef.current !== null) {
      window.clearTimeout(stallRecoveryTimerRef.current)
      stallRecoveryTimerRef.current = null
    }
    setMediaUrlOverride(null)
    setMediaRecoveryAttempt(0)
  }, [playback?.live, playback?.revision, playback?.url])

  useEffect(() => { mediaRecoveryAttemptRef.current = mediaRecoveryAttempt }, [mediaRecoveryAttempt])

  useEffect(() => () => {
    if (recoveryHealthyTimerRef.current !== null) window.clearTimeout(recoveryHealthyTimerRef.current)
    if (stallRecoveryTimerRef.current !== null) window.clearTimeout(stallRecoveryTimerRef.current)
  }, [])

  const clearStallRecoveryTimer = useCallback(() => {
    if (stallRecoveryTimerRef.current !== null) {
      window.clearTimeout(stallRecoveryTimerRef.current)
      stallRecoveryTimerRef.current = null
    }
  }, [])

  // Make the media element visible first. The HLS attachment effect below
  // then runs against the mounted element and keeps it alive across polling.
  useEffect(() => {
    const element = videoRef.current
    const nextKey = `${source.id}|${playbackKey}`
    const sourceChanged = activeSourceRef.current !== source.id
    const playbackMode = live ? 'live' : hlsPlayback ? 'finite-hls' : 'file'
    const playbackTransport = hlsPlayback ? 'hls' : 'file'
    const transportChanged = playbackTransportRef.current !== null && playbackTransportRef.current !== playbackTransport
    const previewWindow = isPreviewWindow(playback)
    const clockChanged = previewWindowRef.current !== null && previewWindowRef.current !== previewWindow

    if (!playback?.url) {
      sourceKeyRef.current = null
      activeSourceRef.current = source.id
      playbackModeRef.current = null
      playbackTransportRef.current = null
      previewWindowRef.current = null
      preserveRef.current = null
      liveEdgeRef.current = null
      // A disconnected desktop capture will hand us a fresh HLS session when
      // its relay reconnects. Treat that session like a first attach so an
      // already-playing viewer does not bypass the 30-second startup runway.
      wantsPlaybackRef.current = false
      userPausedRef.current = false
      setVideoSrc(null)
      setVideoKey(nextKey)
      setState('waiting')
      setError(null)
      setPlaybackBlocked(false)
      clearStallRecoveryTimer()
      setAtLiveEdge(true)
      renderedFrameRef.current = false
      setHasRenderedFrame(false)
      onLoadProgress?.({ value: null, bufferedSeconds: null, targetSeconds: null, playable: false })
      return
    }

    // A retry intentionally revisits the same URL/revision. Normal monitor
    // polls never reach this branch because the URL and session identity stay
    // stable while Hls.js refreshes the playlist. A changed session or a
    // live-to-finite retained preview reaches this branch so the old media
    // attachment is replaced with the new playback identity.
    const changed = sourceKeyRef.current !== nextKey
    if (!changed) return

    // Any changed live HLS identity may initially expose only a few seconds.
    // Reset automatic-play intent so playAtLiveEdge waits for the rolling
    // playlist to rebuild its 30-second runway. Finite HLS intentionally
    // follows the ordinary finite-media path below.
    const liveSessionChanged = live && hlsPlayback && playbackTransportRef.current === 'hls' && !sourceChanged
    const firstFiniteHlsAttach = hlsPlayback && !live && playbackModeRef.current === null

    if (element && Number.isFinite(element.volume)) {
      audioPreferenceRef.current = { volume: Math.max(0, Math.min(1, element.volume)), muted: element.muted }
    }
    const pendingTime = !sourceChanged && preserveRef.current ? preserveRef.current.time : null
    const currentTime = pendingTime ?? (element && Number.isFinite(element.currentTime) ? element.currentTime : 0)
    const shouldResume = !sourceChanged && Boolean(element && (wantsPlaybackRef.current || !element.paused || endedNaturallyRef.current))
    preserveRef.current = live ? null : {
      // A retained preview clock has no verified relation to the archive.
      // Carry play/pause intent into the recording, but start its own clock at zero.
      time: sourceChanged || clockChanged ? 0 : Math.max(0, currentTime),
      // A retained finite HLS preview is immediately replayable from its
      // available start. Later revisions preserve the viewer's play/pause
      // intent just like an ordinary file.
      shouldResume: firstFiniteHlsAttach || shouldResume,
    }
    sourceKeyRef.current = nextKey
    activeSourceRef.current = source.id
    playbackModeRef.current = playbackMode
    playbackTransportRef.current = playbackTransport
    previewWindowRef.current = previewWindow
    if (clockChanged) onTimeUpdate(0)
    setVideoSrc(effectivePlaybackUrl || playback.url)
    setVideoKey(`${nextKey}|${retry}`)
    setState('waiting')
    setError(null)
    setPlaybackBlocked(false)
    clearStallRecoveryTimer()
    onLoadProgress?.({ value: null, bufferedSeconds: null, targetSeconds: null, playable: false })
    endedNaturallyRef.current = false
    renderedFrameRef.current = false
    setHasRenderedFrame(false)
    if (sourceChanged || transportChanged) {
      wantsPlaybackRef.current = false
      userPausedRef.current = false
      liveEdgeRef.current = null
      setAtLiveEdge(true)
    } else if (liveSessionChanged) {
      // Keep an explicit user pause across reconnect; only automatic play
      // needs to be re-armed once the replacement playlist has buffered.
      wantsPlaybackRef.current = false
      liveEdgeRef.current = null
      setAtLiveEdge(true)
    }
  }, [clearStallRecoveryTimer, effectivePlaybackUrl, hlsPlayback, live, onLoadProgress, playbackKey, playback?.url, retry, source.id, videoRef])

  // A reconnect intentionally remounts the media element when the capture
  // session changes. Apply the viewer's volume/mute choice before HLS has a
  // chance to autoplay so a recovered stream cannot come back at full volume.
  useEffect(() => {
    const element = videoRef.current
    const preference = audioPreferenceRef.current
    if (!element || !preference) return
    element.volume = preference.volume
    element.muted = preference.muted
  }, [videoKey, videoRef])

  useEffect(() => {
    if (playback?.url) return
    if (source.status === 'failed' || source.status === 'stopped' || !capturing) setState('empty')
    else setState('waiting')
  }, [capturing, playback?.url, source.status])

  const playAtLiveEdge = useCallback((fromUser = false) => {
    const element = videoRef.current
    if (!element || !viewActiveRef.current) return
    const hls = hlsRef.current
    const target = liveSyncTarget(element, hls, bufferSeconds, maxLatencySeconds)
    if (!fromUser && !wantsPlaybackRef.current && !userPausedRef.current) {
      // A new playlist initially contains only one short segment. Let video
      // accumulate before autoplay instead of immediately exhausting it.
      const edge = seekableLiveEdge(element)
      const start = element.seekable.length ? element.seekable.start(0) : 0
      if (edge == null || edge - start < bufferSeconds) {
        setState('waiting')
        return
      }
    }
    if (target != null && Number.isFinite(target)) {
      programmaticSeekRef.current = true
      try {
        // Keep the safety cushion on initial attach and Go live. Normal
        // playlist refreshes leave playback and the user's scrubber alone.
        const seekableStart = element.seekable.length ? element.seekable.start(0) : null
        const seekableEnd = element.seekable.length ? element.seekable.end(element.seekable.length - 1) : null
        const position = safeLivePlaybackPosition(seekableStart, seekableEnd, target)
        if (position != null) element.currentTime = position
      } catch {
        // The first playlist can expose a seekable range before its first
        // media frame is appended. Hls.js retries at the next level load.
      }
    }
    const needsAutoplayMute = !fromUser && !wantsPlaybackRef.current && !userPausedRef.current
    userPausedRef.current = false
    wantsPlaybackRef.current = true
    setPlaybackBlocked(false)
    clearStallRecoveryTimer()
    endedNaturallyRef.current = false
    setAtLiveEdge(true)
    // Prime the first automatic play silently so Chromium's autoplay policy
    // allows the live monitor to start. A later Go live click must preserve
    // whatever volume choice the viewer made through the toolbar.
    if (needsAutoplayMute) {
      element.muted = true
      audioPreferenceRef.current = {
        volume: Number.isFinite(element.volume) ? Math.max(0, Math.min(1, element.volume)) : 1,
        muted: true,
      }
    }
    void element.play().then(() => {
      // Do not force mute after the first successful play. The viewer can
      // unmute through the toolbar and monitor polling preserves it.
      wantsPlaybackRef.current = true
    }).catch(() => {
      // Autoplay can be blocked. Expose an explicit user playback action.
      wantsPlaybackRef.current = false
      setPlaybackBlocked(true)
      setState(renderedFrameRef.current ? 'ready' : 'waiting')
    })
  }, [bufferSeconds, maxLatencySeconds, clearStallRecoveryTimer, videoRef])

  useEffect(() => {
    // PersistentView pauses hidden media, not the capture. Resume a live
    // monitor at its current window without replacing it or undoing a pause
    // the viewer explicitly chose before navigating away.
    const returning = viewActive && !wasViewActiveRef.current
    wasViewActiveRef.current = viewActive
    if (returning && live && !userPausedRef.current) playAtLiveEdge()
  }, [viewActive, live, playAtLiveEdge])

  // Attach one Hls.js instance for an active capture. Monitor polls update
  // transcript/candidate state, while Hls.js owns playlist refresh cadence.
  useEffect(() => {
    const element = videoRef.current
    const url = hlsPlayback ? videoSrc : null
    if (!element || !url || !videoSrc || !hlsPlayback) {
      hlsRef.current?.destroy()
      hlsRef.current = null
      return
    }

    let disposed = false
    const nativeHls = element.canPlayType('application/vnd.apple.mpegurl') !== ''

    const setPlayerError = (message: string) => {
      if (disposed) return
      setState('error')
      setError(message)
    }

    if (!Hls.isSupported() && !nativeHls) {
      setPlayerError(live ? 'This browser cannot play the live stream.' : 'This browser cannot play the saved preview.')
      return
    }

    if (nativeHls && !Hls.isSupported()) {
      element.src = url
      element.load()
      const preference = audioPreferenceRef.current
      if (preference) {
        element.volume = preference.volume
        element.muted = preference.muted
      } else {
        // Prime native HLS autoplay without losing the default preference
        // when the player is replaced by a later capture session.
        element.muted = true
        audioPreferenceRef.current = {
          volume: Number.isFinite(element.volume) ? Math.max(0, Math.min(1, element.volume)) : 1,
          muted: true,
        }
      }
      return () => {
        disposed = true
        element.removeAttribute('src')
        element.load()
      }
    }

    const hls = new Hls({
      enableWorker: true,
      lowLatencyMode: false,
      startPosition: live ? -1 : 0,
      ...(live ? {
        liveSyncDuration: bufferSeconds,
        liveMaxLatencyDuration: maxLatencySeconds,
        maxLiveSyncPlaybackRate: 1,
        backBufferLength: 30,
        maxBufferLength: 60,
        maxMaxBufferLength: 120,
      } : {}),
      xhrSetup: (xhr) => {
        // Same-origin API requests still send the Motion cookie. A redirected
        // signed S3 segment uses its URL authorization, without cross-origin
        // cookies or a credentialed CORS requirement.
        xhr.withCredentials = false
      },
    })
    hlsRef.current = hls

    const onManifestParsed = () => {
      if (disposed) return
      setError(null)
      if (live && !userPausedRef.current) playAtLiveEdge()
    }
    const onLevelLoaded = (_event: Events, data: LevelLoadedData) => {
      if (disposed || !live || !data.details.live) return
      liveEdgeRef.current = liveSyncTarget(element, hls, bufferSeconds, maxLatencySeconds)
      // Hls.js handles recovery when playback falls outside the live window.
      // Seeking on normal playlist updates would discard our safety buffer.
      if (!wantsPlaybackRef.current && !userPausedRef.current) playAtLiveEdge()
      if (liveEdgeRef.current != null) setAtLiveEdge(liveEdgeRef.current - element.currentTime < 4)
    }
    const onError = (_event: Events, data: ErrorData) => {
      if (disposed || !data.fatal) return
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError()
      else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        // A fatal 403 usually means the signed playlist or one of its
        // redirects expired. startLoad() would keep hammering that same URL,
        // so route it through the bounded authenticated media refresh path.
        const nextAttempt = mediaRecoveryAttemptRef.current + 1
        const next = refreshedMediaUrl(videoSrc, playback?.asset_url || playback?.url, nextAttempt, mediaOrigin())
        if (next && nextAttempt <= MAX_MEDIA_RECOVERY_ATTEMPTS) {
          setMediaRecoveryAttempt(nextAttempt)
          setMediaUrlOverride(next)
          setState('waiting')
          setError(null)
        } else {
          setPlayerError(live ? 'The live preview could not be loaded.' : 'The saved preview could not be loaded.')
        }
      }
      else setPlayerError(live ? 'The live preview could not be loaded.' : 'The saved preview could not be loaded.')
    }

    hls.on(Hls.Events.MANIFEST_PARSED, onManifestParsed)
    hls.on(Hls.Events.LEVEL_LOADED, onLevelLoaded)
    hls.on(Hls.Events.ERROR, onError)
    // Register listeners first, then attach and load directly. Loading via a
    // MEDIA_ATTACHED callback can miss the event in browsers where attachment
    // completes synchronously.
    hls.attachMedia(element)
    hls.loadSource(url)

    return () => {
      disposed = true
      hls.destroy()
      if (hlsRef.current === hls) hlsRef.current = null
    }
  }, [bufferSeconds, maxLatencySeconds, hlsPlayback, live, playback?.asset_url, playback?.url, videoKey, videoRef, videoSrc, playAtLiveEdge])

  const handleLoadedMetadata = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    const element = event.currentTarget
    const preference = audioPreferenceRef.current
    if (preference) {
      element.volume = preference.volume
      element.muted = preference.muted
    }
    onLoadProgress?.(measureMediaLoadProgress(element, live, bufferSeconds))
    const preserve = preserveRef.current
    preserveRef.current = null
    setError(null)

    if (live) {
      liveEdgeRef.current = liveSyncTarget(element, hlsRef.current, bufferSeconds, maxLatencySeconds)
      if (!userPausedRef.current) playAtLiveEdge()
      return
    }

    if (!preserve) return
    const duration = Number.isFinite(element.duration) ? element.duration : playback?.duration || 0
    if (preserve.time > 0 && duration > 0) {
      const target = Math.min(preserve.time, Math.max(0, duration - 0.05))
      try { element.currentTime = target } catch { /* wait until the preview is buffered */ }
    }

    if (preserve.shouldResume) {
      wantsPlaybackRef.current = true
      void element.play().catch(() => { wantsPlaybackRef.current = false })
    }
  }, [bufferSeconds, maxLatencySeconds, live, onLoadProgress, playback?.duration, playAtLiveEdge])

  const handleVolumeChange = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    const element = event.currentTarget
    if (!Number.isFinite(element.volume)) return
    audioPreferenceRef.current = { volume: Math.max(0, Math.min(1, element.volume)), muted: element.muted }
  }, [])

  const handlePlay = useCallback(() => {
    if (!viewActiveRef.current) { videoRef.current?.pause(); return }
    clearStallRecoveryTimer()
    if (mediaRecoveryAttempt > 0) {
      if (recoveryHealthyTimerRef.current !== null) window.clearTimeout(recoveryHealthyTimerRef.current)
      recoveryHealthyTimerRef.current = window.setTimeout(() => {
        recoveryHealthyTimerRef.current = null
        setMediaRecoveryAttempt(0)
      }, HEALTHY_RECOVERY_WINDOW_MS)
    }
    wantsPlaybackRef.current = true
    userPausedRef.current = false
    endedNaturallyRef.current = false
    setPlaybackBlocked(false)
    if (!live) setState('ready')
    const element = videoRef.current
    if (element) onLoadProgress?.({ ...measureMediaLoadProgress(element, live, bufferSeconds), playable: true })
  }, [bufferSeconds, clearStallRecoveryTimer, live, mediaRecoveryAttempt, onLoadProgress, videoRef])

  const handlePlaying = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    // A queued `playing` event can arrive after a native pause. Preserve the
    // explicit pause instead of reviving the monitor from a stale event.
    if (userPausedRef.current || event.currentTarget.paused) return
    clearStallRecoveryTimer()
    renderedFrameRef.current = true
    setHasRenderedFrame(true)
    setPlaybackBlocked(false)
    wantsPlaybackRef.current = true
    userPausedRef.current = false
    setState('ready')
    onLoadProgress?.({ ...measureMediaLoadProgress(event.currentTarget, live, bufferSeconds), playable: true })
  }, [bufferSeconds, clearStallRecoveryTimer, live, onLoadProgress])

  const handleWaiting = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    const element = event.currentTarget
    // `stalled` can describe a network request while buffered video is still
    // playing. Only report buffering when the media actually needs data.
    if (!live || element.readyState >= 3 || element.paused || userPausedRef.current || !wantsPlaybackRef.current) return
    setState('waiting')
    onLoadProgress?.({ ...measureMediaLoadProgress(element, live, bufferSeconds), playable: false })
    if (stallRecoveryTimerRef.current === null) {
      stallRecoveryTimerRef.current = window.setTimeout(() => {
        stallRecoveryTimerRef.current = null
        if (wantsPlaybackRef.current && !userPausedRef.current && !element.paused && element.readyState < 3) setPlaybackBlocked(true)
      }, STALL_RECOVERY_DELAY_MS)
    }
  }, [bufferSeconds, live, onLoadProgress])

  const handleLoadedData = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    renderedFrameRef.current = true
    setHasRenderedFrame(true)
    onLoadProgress?.(measureMediaLoadProgress(event.currentTarget, live, bufferSeconds))
    // A paused first frame is still a usable preview. This is especially
    // important when the browser rejects the initial muted autoplay request.
    if (live && (playbackBlocked || userPausedRef.current)) setState('ready')
  }, [bufferSeconds, live, onLoadProgress, playbackBlocked])

  const handlePause = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    clearStallRecoveryTimer()
    // A navigation pause must not overwrite the viewer's playback intent.
    if (!viewActiveRef.current) return
    if (!event.currentTarget.ended && !endedNaturallyRef.current && !programmaticSeekRef.current) {
      wantsPlaybackRef.current = false
      userPausedRef.current = true
      setPlaybackBlocked(false)
      if (renderedFrameRef.current) setState('ready')
    }
  }, [clearStallRecoveryTimer])

  const handleSeeking = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    programmaticSeekRef.current = false
    if (!live) return
    const edge = liveEdgeRef.current ?? seekableLiveEdge(event.currentTarget)
    if (edge != null && edge - event.currentTarget.currentTime > 4) {
      setAtLiveEdge(false)
    }
  }, [live])

  const handleEnded = useCallback(() => {
    endedNaturallyRef.current = true
  }, [])

  const handleError = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    if (hlsPlayback && hlsRef.current) return
    const mediaError = event.currentTarget.error
    const nextAttempt = mediaRecoveryAttempt + 1
    const next = refreshedMediaUrl(videoSrc, playback?.asset_url || playback?.url, nextAttempt, mediaOrigin())
    if (next && nextAttempt <= MAX_MEDIA_RECOVERY_ATTEMPTS) {
      preserveRef.current = preservePlayback(event.currentTarget, wantsPlaybackRef.current)
      setMediaRecoveryAttempt(nextAttempt)
      setMediaUrlOverride(next)
      setState('waiting')
      setError(null)
      return
    }
    setState('error')
    setError(mediaError?.message || 'The captured preview could not be loaded.')
  }, [hlsPlayback, live, mediaRecoveryAttempt, playback?.asset_url, playback?.url, videoSrc])

  const handleTimeUpdate = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    const element = event.currentTarget
    onLoadProgress?.(measureMediaLoadProgress(element, live, bufferSeconds))
    onTimeUpdate(element.currentTime)
    if (!live) return
    if (!element.paused && element.readyState >= 3 && renderedFrameRef.current) {
      clearStallRecoveryTimer()
      setPlaybackBlocked(false)
      setState('ready')
    }
    const edge = liveSyncTarget(element, hlsRef.current, bufferSeconds, maxLatencySeconds)
    if (edge == null) return
    liveEdgeRef.current = edge
    setAtLiveEdge(edge - element.currentTime < 4)
  }, [bufferSeconds, maxLatencySeconds, clearStallRecoveryTimer, live, onLoadProgress, onTimeUpdate])

  const handleProgress = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    const element = event.currentTarget
    onLoadProgress?.(measureMediaLoadProgress(element, live, bufferSeconds))
    if (live && !wantsPlaybackRef.current && !userPausedRef.current) playAtLiveEdge()
  }, [bufferSeconds, live, onLoadProgress, playAtLiveEdge])

  const handleCanPlay = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    if (mediaRecoveryAttempt > 0) {
      if (recoveryHealthyTimerRef.current !== null) window.clearTimeout(recoveryHealthyTimerRef.current)
      recoveryHealthyTimerRef.current = window.setTimeout(() => {
        recoveryHealthyTimerRef.current = null
        setMediaRecoveryAttempt(0)
      }, HEALTHY_RECOVERY_WINDOW_MS)
    }
    onLoadProgress?.({ ...measureMediaLoadProgress(event.currentTarget, live, bufferSeconds), playable: true })
    if (!live) setState('ready')
    else if (event.currentTarget.paused && (playbackBlocked || userPausedRef.current)) {
      renderedFrameRef.current = true
      setHasRenderedFrame(true)
      setState('ready')
    }
  }, [bufferSeconds, live, mediaRecoveryAttempt, onLoadProgress, playbackBlocked])

  const retryPlayback = useCallback(() => {
    setPlaybackBlocked(false)
    playAtLiveEdge(true)
  }, [playAtLiveEdge])

  const retryPreview = useCallback(() => {
    const element = videoRef.current
    // Restart the existing HLS loader in place whenever the authenticated
    // playlist is still current. Remounting the video discards its buffered
    // 30-second safety runway and makes a transient preview stall worse.
    if (live && !mediaUrlOverride && hlsRef.current) {
      setPlaybackBlocked(false)
      setState('waiting')
      setError(null)
      clearStallRecoveryTimer()
      mediaRecoveryAttemptRef.current = 0
      setMediaRecoveryAttempt(0)
      hlsRef.current.stopLoad()
      hlsRef.current.startLoad(-1)
      if (element) {
        const preference = audioPreferenceRef.current
        if (preference) {
          element.volume = preference.volume
          element.muted = preference.muted
        }
        wantsPlaybackRef.current = true
        void element.play().catch(() => setPlaybackBlocked(true))
      }
      return
    }
    if (element) {
      preserveRef.current = live ? null : {
        time: Number.isFinite(element.currentTime) ? Math.max(0, element.currentTime) : 0,
        shouldResume: Boolean(wantsPlaybackRef.current || !element.paused || endedNaturallyRef.current),
      }
    }
    setMediaUrlOverride(null)
    setMediaRecoveryAttempt(0)
    sourceKeyRef.current = null
    setRetry((value) => value + 1)
  }, [bufferSeconds, clearStallRecoveryTimer, live, mediaUrlOverride, videoRef])

  const emptyLabel = source.status === 'failed'
    ? (source.error || 'Capture failed before a video preview was available.')
    : source.status === 'stopped'
      ? 'No video preview is currently available.'
      : 'No video preview is available for this source.'
  const title = `${source.title} preview`
  const togglePlayback = () => {
    const element = videoRef.current
    if (!element || state === 'error') return
    if (!element.paused) {
      clearStallRecoveryTimer()
      wantsPlaybackRef.current = false
      userPausedRef.current = true
      element.pause()
      setPlaybackBlocked(false)
      if (renderedFrameRef.current) setState('ready')
    } else {
      userPausedRef.current = false
      wantsPlaybackRef.current = true
      void element.play().catch(() => {
        wantsPlaybackRef.current = false
        setPlaybackBlocked(true)
      })
    }
  }

  return (
    <div className="home-player-video-surface live-source-player">
      {videoSrc ? (
        <video
          key={videoKey}
          ref={videoRef}
          className={`home-player-video${hasRenderedFrame || state === 'ready' ? ' live-source-video-ready' : ''}`}
          src={hlsPlayback ? undefined : videoSrc}
          poster={source.thumbnail_url || undefined}
          playsInline
          preload="auto"
          aria-label={siteText(title)}
          role="button"
          aria-description="Play or pause preview"
          tabIndex={0}
          onClick={togglePlayback}
          onKeyDown={event => { if (!event.repeat && (event.key === ' ' || event.key === 'Enter')) { event.preventDefault(); togglePlayback() } }}
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={handleLoadedData}
          onCanPlay={handleCanPlay}
          onPlaying={handlePlaying}
          onWaiting={handleWaiting}
          onStalled={handleWaiting}
          onProgress={handleProgress}
          onPlay={handlePlay}
          onVolumeChange={handleVolumeChange}
          onPause={handlePause}
          onSeeking={handleSeeking}
          onEnded={handleEnded}
          onError={handleError}
          onTimeUpdate={handleTimeUpdate}
        />
      ) : null}

      {live && videoSrc && state === 'ready' && !atLiveEdge ? (
        <div className="live-source-player-overlay" aria-live="polite">
          <button type="button" className="live-source-player-go-live" onClick={() => playAtLiveEdge(true)}>Go live</button>
        </div>
      ) : null}

      {state === 'waiting' && !hasRenderedFrame ? (
        <div className="home-player-video-state live-source-loading" role="status" aria-live="polite">
          <div className="live-connection-signal" aria-hidden="true">
            <div className="live-connection-track live-connection-track-in" />
            <div className="live-connection-mark"><i /><i /><i /></div>
            <div className="live-connection-track live-connection-track-out" />
          </div>
          <span className="live-connection-label">{siteText(capturing || live ? 'Connecting' : 'Loading preview')}</span>
        </div>
      ) : null}

      {state === 'waiting' && hasRenderedFrame ? (
        <div className="live-source-buffering" role="status" aria-live="polite">
          <LoaderCircle className="spin" size={14} aria-hidden="true" />
          <span>Buffering preview</span>
        </div>
      ) : null}

      {live && videoSrc && playbackBlocked ? (
        <div className="live-source-player-overlay live-source-player-playback-overlay" aria-live="polite">
          <button type="button" className="live-source-player-go-live" onClick={retryPlayback}>{hasRenderedFrame ? 'Resume preview' : 'Play live preview'}</button>
        </div>
      ) : null}

      {state === 'empty' ? (
        <div className="home-player-video-state home-player-video-empty" role="status">
          <strong>{siteText(source.status === 'failed' ? 'Capture failed' : source.status === 'stopped' ? 'Capture stopped' : 'No preview yet')}</strong>
          <span>{siteText(emptyLabel)}</span>
          {source.status === 'failed' && onRetryCapture ? (
            <button type="button" className="home-player-retry" onClick={onRetryCapture} disabled={retryingCapture}>
              {retryingCapture ? <LoaderCircle className="spin" size={15} aria-hidden="true" /> : <RefreshCw size={15} aria-hidden="true" />}
              {siteText(retryingCapture ? 'Retrying connection…' : 'Retry connection')}
            </button>
          ) : null}
        </div>
      ) : null}

      {state === 'error' ? (
        <div className="home-player-video-state home-player-video-error" role="alert">
          <strong>Preview unavailable</strong>
          <span>{siteText(error || 'The captured video could not be loaded.')}</span>
          <button type="button" className="home-player-retry" onClick={retryPreview}>
            <RefreshCw size={15} aria-hidden="true" />
            Retry preview
          </button>
        </div>
      ) : null}
    </div>
  )
}
